import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Send, X, RotateCcw, MessageCircleQuestion } from "lucide-react";

const STORAGE_KEY = "msc_assistant_history_v1";
const OPEN_KEY = "msc_assistant_open";
const MAX_HISTORY = 24; // client keeps last N messages
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Convert a small set of markdown-ish tokens (**bold**, `code`, newlines) into
 * safe React nodes. We deliberately DON'T support links / images / html to keep
 * XSS surface = 0. The LLM was told to use only these primitives.
 */
function renderMarkdownish(text) {
  if (!text) return null;
  const lines = text.split("\n");
  return lines.map((line, li) => {
    // Tokenize by ** ** and ` ` in a single pass.
    const parts = [];
    let cursor = 0;
    const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
    let match;
    while ((match = re.exec(line)) !== null) {
      if (match.index > cursor) {
        parts.push({ t: "text", v: line.slice(cursor, match.index) });
      }
      const tok = match[0];
      if (tok.startsWith("**")) {
        parts.push({ t: "bold", v: tok.slice(2, -2) });
      } else {
        parts.push({ t: "code", v: tok.slice(1, -1) });
      }
      cursor = match.index + tok.length;
    }
    if (cursor < line.length) parts.push({ t: "text", v: line.slice(cursor) });

    return (
      <span key={li}>
        {parts.map((p, pi) => {
          if (p.t === "bold") {
            return (
              <strong key={pi} className="font-semibold text-ink">
                {p.v}
              </strong>
            );
          }
          if (p.t === "code") {
            return (
              <code
                key={pi}
                className="font-mono text-[12px] px-1 py-0.5 rounded-sm bg-bg border border-line text-cyan"
              >
                {p.v}
              </code>
            );
          }
          return <span key={pi}>{p.v}</span>;
        })}
        {li < lines.length - 1 && <br />}
      </span>
    );
  });
}

const SUGGESTED_PROMPTS = {
  admin: [
    "Break down my commission on a $299 sub.",
    "How do I go live on Stripe?",
    "How do I add an office manager?",
  ],
  office_manager: [
    "How is my OM pool calculated?",
    "How much do I earn on a $299 sub?",
    "Which rep is contributing the most this month?",
  ],
  rep: [
    "How do I send a proposal?",
    "The owner said 'too expensive' — what do I say?",
    "How is my commission calculated?",
  ],
};

export default function AssistantWidget() {
  const { user } = useAuth();
  const location = useLocation();

  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(OPEN_KEY) === "1";
    } catch (e) {
      return false;
    }
  });
  const [messages, setMessages] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch (e) {
      // ignore
    }
    return [];
  });
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const abortRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  // Persist
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-MAX_HISTORY)));
    } catch (e) {
      // quota — drop silently
    }
  }, [messages]);
  useEffect(() => {
    try {
      localStorage.setItem(OPEN_KEY, open ? "1" : "0");
    } catch (e) {
      // ignore
    }
  }, [open]);

  // Autoscroll on new tokens
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  // Focus input on open
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const send = useCallback(
    async (text) => {
      const trimmed = (text ?? input).trim();
      if (!trimmed || streaming) return;
      setInput("");

      const history = messages.slice(-MAX_HISTORY);
      const nextMessages = [
        ...messages,
        { role: "user", content: trimmed },
        { role: "assistant", content: "" }, // placeholder for stream
      ];
      setMessages(nextMessages);
      setStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const res = await fetch(`${BACKEND_URL}/api/assistant/chat`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("msc_access_token") || ""}`,
          },
          body: JSON.stringify({
            message: trimmed,
            history: history,
            page: location.pathname,
          }),
          signal: controller.signal,
        });

        if (!res.ok || !res.body) {
          throw new Error(`Assistant unavailable (${res.status})`);
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let assistantText = "";

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          // Parse SSE frames
          const frames = buffer.split("\n\n");
          buffer = frames.pop() || ""; // keep incomplete frame

          for (const frame of frames) {
            const lines = frame.split("\n");
            const eventLine = lines.find((l) => l.startsWith("event: "));
            const dataLine = lines.find((l) => l.startsWith("data: "));

            // Mid-stream error frame from the backend
            if (eventLine && eventLine.slice(7).trim() === "error") {
              setMessages((prev) => {
                const copy = [...prev];
                if (copy.length && copy[copy.length - 1].role === "assistant") {
                  copy[copy.length - 1] = {
                    role: "assistant",
                    content:
                      "**I couldn't finish that reply.** The assistant hit an error mid-stream — try again in a moment.",
                  };
                }
                return copy;
              });
              try { reader.cancel(); } catch (_e) { /* ignore */ }
              break;
            }

            if (!dataLine) continue;
            const payload = dataLine.slice(6);
            if (payload === "[DONE]") continue;
            // Un-escape newlines (server encodes \n)
            const chunk = payload.replace(/\\n/g, "\n");
            assistantText += chunk;
            setMessages((prev) => {
              const copy = [...prev];
              copy[copy.length - 1] = { role: "assistant", content: assistantText };
              return copy;
            });
          }
        }
      } catch (err) {
        if (err.name === "AbortError") {
          setMessages((prev) => {
            const copy = [...prev];
            if (copy.length && copy[copy.length - 1].role === "assistant") {
              const partial = copy[copy.length - 1].content;
              copy[copy.length - 1] = {
                role: "assistant",
                content: partial + (partial ? "\n\n" : "") + "**(stopped)**",
              };
            }
            return copy;
          });
        } else {
          setMessages((prev) => {
            const copy = [...prev];
            if (copy.length && copy[copy.length - 1].role === "assistant") {
              copy[copy.length - 1] = {
                role: "assistant",
                content:
                  "**I couldn't reach the assistant just now.** Try again in a moment — if it keeps failing, the LLM key may be out of balance.",
              };
            }
            return copy;
          });
        }
      } finally {
        setStreaming(false);
        abortRef.current = null;
      }
    },
    [input, messages, streaming, location.pathname]
  );

  const stop = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  const clear = () => {
    setMessages([]);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // ignore
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Don't render for unauthenticated or password-reset users
  if (!user || user.must_change_password) return null;

  const role = user.role || "rep";
  const suggestions = SUGGESTED_PROMPTS[role] || SUGGESTED_PROMPTS.rep;

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="assistant-open-btn"
          className="fixed bottom-5 right-5 z-40 flex items-center gap-2 bg-cyan text-bg font-medium py-2.5 px-4 rounded-full shadow-xl shadow-cyan/20 hover:bg-cyan-hover hover:shadow-cyan/40 transition-all active:scale-[0.97] group"
        >
          <Sparkles size={15} className="group-hover:animate-pulse" />
          <span className="text-sm">Ask Coach</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="assistant-panel"
          className="fixed bottom-5 right-5 z-40 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-6rem)] bg-bg-surface border border-line rounded-sm shadow-2xl flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-line flex items-center gap-2 bg-bg">
            <div className="h-8 w-8 rounded-sm bg-cyan/10 border border-cyan/40 flex items-center justify-center">
              <Sparkles size={15} className="text-cyan" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-ink leading-tight">
                Main Street Coach
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mt-0.5">
                Grounded on your playbook
              </div>
            </div>
            {messages.length > 0 && (
              <button
                onClick={clear}
                data-testid="assistant-clear-btn"
                className="text-ink-muted hover:text-ink p-1.5 rounded-sm hover:bg-bg-hover"
                title="Start fresh"
              >
                <RotateCcw size={13} />
              </button>
            )}
            <button
              onClick={() => setOpen(false)}
              data-testid="assistant-close-btn"
              className="text-ink-muted hover:text-ink p-1.5 rounded-sm hover:bg-bg-hover"
            >
              <X size={15} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={listRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
            data-testid="assistant-messages"
          >
            {messages.length === 0 && (
              <div className="space-y-4">
                <div className="border border-line rounded-sm bg-bg p-3">
                  <div className="flex items-start gap-2">
                    <MessageCircleQuestion size={14} className="text-cyan mt-0.5 shrink-0" />
                    <div className="text-[13px] text-ink-secondary leading-relaxed">
                      I help with the app, sales technique, and commission math.
                      Ask me anything about a lead, the playbook, or a feature.
                    </div>
                  </div>
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
                  Try one of these
                </div>
                <div className="space-y-2">
                  {suggestions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => send(q)}
                      data-testid={`assistant-suggestion-${i}`}
                      className="w-full text-left border border-line rounded-sm bg-bg hover:border-cyan/60 hover:bg-cyan/5 transition-colors px-3 py-2 text-[13px] text-ink"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={m.role === "user" ? "flex justify-end" : "flex justify-start"}
                data-testid={`assistant-msg-${m.role}-${i}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-sm text-[13px] leading-relaxed ${
                    m.role === "user"
                      ? "bg-cyan/15 border border-cyan/30 text-ink"
                      : "bg-bg border border-line text-ink"
                  }`}
                >
                  {m.role === "assistant" && m.content === "" && streaming ? (
                    <TypingDots />
                  ) : (
                    renderMarkdownish(m.content)
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Input */}
          <div className="border-t border-line p-3 bg-bg">
            <div className="flex gap-2 items-end">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={streaming ? "Coach is typing…" : "Ask about a lead, script, or feature…"}
                rows={2}
                data-testid="assistant-input"
                disabled={streaming}
                className="flex-1 bg-bg-surface border border-line rounded-sm px-3 py-2 text-[13px] text-ink focus:border-cyan transition-colors resize-none disabled:opacity-60"
              />
              {streaming ? (
                <button
                  onClick={stop}
                  data-testid="assistant-stop-btn"
                  className="shrink-0 bg-bg-surface border border-line text-ink hover:border-ink-muted p-2 rounded-sm transition-colors self-stretch"
                  title="Stop"
                >
                  <X size={16} />
                </button>
              ) : (
                <button
                  onClick={() => send()}
                  disabled={!input.trim()}
                  data-testid="assistant-send-btn"
                  className="shrink-0 bg-cyan text-bg p-2 rounded-sm hover:bg-cyan-hover transition-colors disabled:opacity-30 self-stretch"
                  title="Send"
                >
                  <Send size={16} />
                </button>
              )}
            </div>
            <div className="text-[10px] text-ink-muted mt-1.5 flex items-center gap-1">
              <span>Enter to send · Shift+Enter for newline</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1" data-testid="assistant-typing">
      <span className="h-1.5 w-1.5 rounded-full bg-ink-muted animate-bounce" style={{ animationDelay: "0ms" }} />
      <span className="h-1.5 w-1.5 rounded-full bg-ink-muted animate-bounce" style={{ animationDelay: "120ms" }} />
      <span className="h-1.5 w-1.5 rounded-full bg-ink-muted animate-bounce" style={{ animationDelay: "240ms" }} />
    </span>
  );
}
